
import { api, apiGet, apiPost } from "../API";

const _BASE_LKP = "/adriva_api/lookup";

export interface EnrichedLookupCandidate {
  family: "lookup_field" | "code_description_pair" | "dependent_lookup";
  status: string;
  candidate_id: string;
  confidence?: number;
  provenance?: string;
  proven_on_rows?: boolean;
  review?: Record<string, any>;
  field?: { table: string; field: string };
  code?: { table: string; field: string };
  description?: { table: string; field: string };
  determinant?: { table: string; field: string };
  dependent?: { table: string; field: string };
  evidence?: { references?: string[]; rationale?: string; label?: string };
  // materialized values
  observed_values?: string[];
  observed_distinct?: number;
  values_source?: "rows" | "profile";
  code_to_description?: Record<string, string>;
  pair_stats?: { has_rows: boolean; rows?: number; code_count?: number;
                 single_description?: number; conflicting?: number; functional?: boolean };
  value_map?: Record<string, string[]>;
  dependency_stats?: { has_rows: boolean; rows?: number; determinant_values?: number;
                       functional?: boolean; support?: Record<string, number> };
}

export interface CandidateAcceptDecision {
  candidate_id: string;
  decision: "ACCEPT" | "REJECT";
  code_to_description?: Record<string, string>;   // SME override for lookup_field / pair
  note?: string;
}





// ── Types ──────────────────────────────────────────────────────────────────────

export interface LookupFieldRef { table: string; field: string; }

export interface LookupCandidate {
  family: "lookup_field" | "code_description_pair" | "dependent_lookup";
  status: string;
  provenance: string;
  candidate_id: string;
  confidence?: number;
  proven_on_rows?: boolean;
  review?: Record<string, any>;
  evidence?: { references?: string[]; rationale?: string; label?: string };
  // family-specific
  field?: LookupFieldRef;
  code?: LookupFieldRef;
  description?: LookupFieldRef;
  determinant?: LookupFieldRef;
  dependent?: LookupFieldRef;
}

export interface LookupCandidatesBlock {
  contract_version?: string;
  generated_from_profile?: boolean;
  items: LookupCandidate[];
  knowledge_gaps?: string[];
  summary?: { total: number; by_family: Record<string, number> };
}

export interface AuthoritativeDefinition {
    table: string; column: string; business_name?: string;
    logical_field: string | null;                 // <-- now nullable (UNMATCHED)
    suggested_logical_field?: string | null;      // <-- ADD (name-only guess)
    governed_values: string[];
    code_to_description: Record<string, string>;
    mapping?: {
      score: number; value_containment?: number; name_similarity?: number;
      domain_code_count?: number; field_distinct_count?: number;   // <-- ADD
      runner_up?: { logical_field: string; score: number } | null; method?: string;
    };
    justification?: string;                        // <-- ADD (plain-English reason)
    review_reasons?: string[];                     // <-- ADD
    provenance: string;
    review?: { status: "RESOLVED" | "REVIEW" | "UNMATCHED" | "REJECTED"; confirmed: boolean };
  }

export interface ClientDomain { logical_field: string; code_count: number;
                                code_to_description: Record<string,string>; }

export interface AuthoritativeBlock {
  contract_version?: string;
  logical_field_count?: number;
  definitions: AuthoritativeDefinition[];
  summary?: { resolved: number; needs_review: number; unmatched?: number;
              unmapped_columns: number; total_definitions: number };
  client_domains?: ClientDomain[];
}



export interface CanonicalLookupRecord {
  key: string; table: string; column: string;
  status: "CONFIRMED" | "AUTHORITATIVE_ONLY" | "DISCOVERED_ONLY" | "CANONICAL" | "PENDING_REVIEW";
  business_name?: string; logical_field?: string; discovered_confidence?: number;
  code_to_description?: Record<string, string>;
  provenance?: string[];
  mapping?: any;
  conformance?: {
    observed_distinct: number; governed_distinct: number;
    values_not_in_reference: string[]; values_not_in_reference_count: number;
    governed_values_unused_count: number; conformant: boolean;
  };
  review?: Record<string, any>;
  governed?: boolean; 
  exportable?: boolean;
}

export interface CanonicalBlock {
  contract_version?: string;
  lookup_fields: CanonicalLookupRecord[];
  code_description_pairs: LookupCandidate[];
  dependent_lookups: LookupCandidate[];
  summary?: {
    lookup_fields: number; by_status: Record<string, number>;
    code_description_pairs: number; dependent_lookups: number;
  };
}

export interface LookupOverview {
  success: boolean; entity_id: string;
  step1: { present: boolean; summary: any };
  step2: { present: boolean; summary: any };
  step3: { present: boolean; summary: any };
}

export interface CandidateReview {
  candidate_id: string;
  status: "ACCEPTED" | "REJECTED" | "PROPOSED";
  note?: string;
}

export interface MappingReview {
  key: string;                         // "TABLE.COLUMN"
  status: "RESOLVED" | "REVIEW" | "REJECTED";
  logical_field?: string;              // SME override
  note?: string;
}

export interface Step1Decision {
  candidate_id: string;
  decision: "ACCEPT" | "REJECT" | "RESET" | "UPDATE";
  entity_name?: string;
  note?: string;
}
export async function lookup_saveStep1Review(
  entityId: string, decisions: Step1Decision[], reviewedBy = "SME",
): Promise<{ success: boolean; applied: number; renamed: number; unknown: string[];
            by_status: Record<string, number>; candidates: LookupCandidatesBlock }> {
  return apiPost(`${_BASE_LKP}/candidates/step1/${encodeURIComponent(entityId)}`, {
    decisions, reviewed_by: reviewedBy,
  });
}


// ── Read ────────────────────────────────────────────────────────────────────────

export async function lookup_getOverview(entityId: string): Promise<LookupOverview> {
  return apiGet(`${_BASE_LKP}/overview/${encodeURIComponent(entityId)}`);
}

export async function lookup_getCandidates(
  entityId: string,
): Promise<{ success: boolean; data: LookupCandidatesBlock }> {
  return apiGet(`${_BASE_LKP}/candidates/${encodeURIComponent(entityId)}`);
}

// ── Step 2 reference DIFF (re-upload → review → accept) ──────────────────────
export interface RefDiffValue { code: string; display?: string; governance_status?: string; description?: string; }
export interface RefDiffNewColumn {
  logical_field?: string; suggested_table?: string; suggested_column?: string;
  business_name?: string; value_count: number; values: RefDiffValue[]; reason?: string[];
}
export interface RefDiffStatusChange {
  entity_id: string; entity_name: string; field_id?: string; code: string; display?: string;
  from_status: string; to_status: string; currently_promoted?: boolean;
}
export interface RefDiffNewValue {
  entity_id: string; entity_name: string; field_id?: string;
  code: string; display?: string; governance_status?: string; description?: string;
}
export interface RefDiffValueGroup {
  entity_id: string; entity_name: string; field_id?: string; total: number;
  status_changes: RefDiffStatusChange[]; new_values: RefDiffNewValue[];
}
export interface RefDiffResult {
  success: boolean; entity_id: string;
  summary: { new_columns: number; status_changes: number; new_values: number; fields_with_changes?: number };
  new_columns: RefDiffNewColumn[]; status_changes: RefDiffStatusChange[]; new_values: RefDiffNewValue[];
  value_groups?: RefDiffValueGroup[];
  desc_backfill?: any[];
}

export async function lookup_diffReference(appNm: string, entityId: string): Promise<RefDiffResult> {
  return apiPost(`${_BASE_LKP}/diff-reference`, { app_nm: appNm, entity_id: entityId });
}

export async function lookup_applyReferenceDiff(entityId: string, reviewedBy: string, accepted: {
  new_columns?: any[]; status_changes?: any[]; new_values?: any[]; desc_backfill?: any[];
}): Promise<{ success: boolean; applied: Record<string, number> }> {
  return apiPost(`${_BASE_LKP}/apply-reference-diff`, {
    entity_id: entityId, reviewed_by: reviewedBy,
    new_columns: accepted.new_columns || [], status_changes: accepted.status_changes || [],
    new_values: accepted.new_values || [], desc_backfill: accepted.desc_backfill || [],
  });
}

export async function lookup_getAuthoritative(
  entityId: string,
): Promise<{ success: boolean; data: AuthoritativeBlock }> {
  return apiGet(`${_BASE_LKP}/authoritative/${encodeURIComponent(entityId)}`);
}

export async function lookup_getCanonical(
  entityId: string,
): Promise<{ success: boolean; data: CanonicalBlock }> {
  return apiGet(`${_BASE_LKP}/canonical/${encodeURIComponent(entityId)}`);
}

// ── Run steps ─────────────────────────────────────────────────────────────────

export async function lookup_detect(
  appNm: string, entityId: string,
): Promise<{ success: boolean; data: LookupCandidatesBlock }> {
  return apiPost(`${_BASE_LKP}/detect`, { app_nm: appNm, entity_id: entityId });
}

/** Upload the client value-reference workbook (multipart). */
export async function lookup_uploadReference(
  appNm: string, file: File,
): Promise<{ success: boolean; app_nm: string; reference_path: string; filename: string }> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await api.post(
    `${_BASE_LKP}/upload-reference/${encodeURIComponent(appNm)}`, form,
  );
  return res.data;
}

// Optional Step-2 input. kind = "dictionary" (physical->logical). When provided,
// the build uses the dictionary-first path (LOV comes from the reference workbook).
// Omit it and behavior is unchanged.
export async function lookup_uploadReferenceKind(
  appNm: string, kind: "reference" | "dictionary", file: File,
): Promise<{ success: boolean; app_nm: string; kind: string; path: string; filename: string }> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await api.post(
    `${_BASE_LKP}/upload-reference/${encodeURIComponent(appNm)}/${encodeURIComponent(kind)}`, form,
  );
  return res.data;
}

export async function lookup_buildAuthoritative(
  appNm: string, entityId: string, referencePath?: string, loadRows = true,
  dictionaryPath?: string,
): Promise<{ success: boolean; data: AuthoritativeBlock }> {
  return apiPost(`${_BASE_LKP}/authoritative`, {
    app_nm: appNm, entity_id: entityId, reference_path: referencePath, load_rows: loadRows,
    dictionary_path: dictionaryPath,
  });
}

export async function lookup_reconcile(
  appNm: string, entityId: string, checkConformance = true,
): Promise<{ success: boolean; data: CanonicalBlock }> {
  return apiPost(`${_BASE_LKP}/reconcile`, {
    app_nm: appNm, entity_id: entityId, check_conformance: checkConformance,
  });
}

// ── SME review ──────────────────────────────────────────────────────────────────

export async function lookup_getLogicalFields(
  entityId: string,
): Promise<{ success: boolean; logical_fields: string[] }> {
  return apiGet(`${_BASE_LKP}/logical-fields/${encodeURIComponent(entityId)}`);
}

export async function lookup_saveReview(
  entityId: string,
  candidate_decisions: CandidateReview[] = [],
  mapping_decisions: MappingReview[] = [],
): Promise<{ success: boolean; updated: number }> {
  return apiPost(`${_BASE_LKP}/review/${encodeURIComponent(entityId)}`, {
    candidate_decisions, mapping_decisions,
  });
}


export type FieldDecision = "confirm" | "not_a_lookup" | "retype" | "reopen";

export interface FieldValueEdit {
  key: string;                                   // "TABLE.COLUMN"
  decision: FieldDecision;
  code_to_description?: Record<string, string>;  // SME code -> label (merged on confirm)
  logical_field?: string;                        // SME override of the logical name
  retype_target?: "range" | "format";            // when decision === "retype"
  note?: string;
}

export interface FieldDetailResponse {
  success: boolean;
  entity_id: string;
  key: string;
  candidate: LookupCandidate | null;
  authoritative: AuthoritativeDefinition | null;
  canonical: CanonicalLookupRecord | null;
  conformance: CanonicalLookupRecord["conformance"] | null;
}

/** Full three-layer detail for one field (candidate + authoritative + canonical). */
export async function lookup_getFieldDetail(
  entityId: string, key: string,
): Promise<FieldDetailResponse> {
  return apiGet(`${_BASE_LKP}/field/${encodeURIComponent(entityId)}/${encodeURIComponent(key)}`);
}

/** Persist SME code-value edits + decisions. Returns the refreshed canonical block. */
export async function lookup_saveFieldValues(
  entityId: string,
  edits: FieldValueEdit[],
  reviewedBy = "SME",
): Promise<{ success: boolean; applied: number; unknown_keys: string[];
            status_counts: Record<string, number>; data: CanonicalBlock }> {
  return apiPost(`${_BASE_LKP}/field-values/${encodeURIComponent(entityId)}`, {
    edits, reviewed_by: reviewedBy,
  });
}


/** Accept/reject Step-1 candidates. Accepting materializes values onto the
 *  canonical model (governed value list) or confirms a dependency. */
export async function lookup_acceptCandidates(
  entityId: string,
  decisions: CandidateAcceptDecision[],
  reviewedBy = "SME",
): Promise<{
  success: boolean; applied: number; materialized_fields: string[];
  confirmed_dependencies: number; unknown_candidates: string[];
  candidates: LookupCandidatesBlock; canonical: CanonicalBlock;
}> {
  return apiPost(`${_BASE_LKP}/candidates/accept/${encodeURIComponent(entityId)}`, {
    decisions, reviewed_by: reviewedBy,
  });
}


export async function lookup_downloadReview(
  entityId: string, fmt: "xlsx" | "html" | "csv" = "xlsx", appNm = "",
): Promise<void> {
  const url = `${_BASE_LKP}/export/${encodeURIComponent(entityId)}?fmt=${fmt}&app_nm=${encodeURIComponent(appNm)}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`export failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : `Lookup_Review_${entityId}.${fmt}`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// ── Propagate a reference change downstream ─────────────────────────────────
// The lookup layer is the single reference authority. This pushes the current READY
// reference concepts into Enterprise Knowledge AND re-guards Business Control in one call.

export interface PublishReferenceResult {
  success: boolean;
  entity_id: string;
  enterprise_knowledge: { success: boolean; reference_domains?: number; values?: number; note?: string; error?: string };
  business_control: {
    success: boolean; skipped?: boolean; reason?: string;
    controls_before?: number; controls_after?: number;
    remapped?: number; dropped?: number; reflagged?: number;
  };
  note?: string;
}

/** Refresh Enterprise Knowledge and Business Control from the current lookup reference data. */
export async function lookup_publishReference(entityId: string): Promise<PublishReferenceResult> {
  return apiPost(`${_BASE_LKP}/${encodeURIComponent(entityId)}/publish-reference`, {});
}

// ── Lookup → EKD promotion ──────────────────────────────────────────────────
export async function lookup_promote(entityId: string, items: {entity_id:string;code:string}[], by: string): Promise<any> {
  return apiPost(`${_BASE_LKP}/${encodeURIComponent(entityId)}/promote`, { entity_id: entityId, items, reviewed_by: by });
}
export async function lookup_depromote(entityId: string, items: {entity_id:string;code:string}[], by: string): Promise<any> {
  return apiPost(`${_BASE_LKP}/${encodeURIComponent(entityId)}/depromote`, { entity_id: entityId, items, reviewed_by: by });
}
export async function lookup_promoteAllInEntity(entityId: string, targetEntityId: string, onlyStatus: string | null, by: string): Promise<any> {
  return apiPost(`${_BASE_LKP}/${encodeURIComponent(entityId)}/promote-all-in-entity`, { entity_id: targetEntityId, only_status: onlyStatus, reviewed_by: by });
}
export async function lookup_promotionSummary(entityId: string): Promise<any> {
  return apiGet(`${_BASE_LKP}/${encodeURIComponent(entityId)}/promotion-summary`);
}
export async function lookup_syncPrecheck(entityId: string): Promise<{ success: boolean; at_risk_fields: any[]; count: number }> {
  return apiGet(`${_BASE_LKP}/${encodeURIComponent(entityId)}/sync-precheck`);
}
export interface PromotionCoverage { fields: { field_id: string; rows_populated: number; rows_valid: number; valid_pct: number | null; complete_list: boolean }[] }
export interface PromotionPreviewField {
  entity_id: string; entity_name: string; canonical_count: number; would_promote: number;
  by_status?: Record<string, number>; allowed?: string[]; overridden?: boolean;
  // PHASE I
  observed_only?: boolean; accepted?: boolean; meaning?: string;
  data_coverage?: PromotionCoverage | null;          // observed lists
  data_coverage_now?: PromotionCoverage | null;      // governed: rows a lookup rule passes today
  data_coverage_after?: PromotionCoverage | null;    // governed: ... with the chosen statuses
}
export interface PromotionPreview {
  success: boolean;
  governed_fields: PromotionPreviewField[];
  unstatused_fields: PromotionPreviewField[];
  empty_fields: PromotionPreviewField[];
  empty_count: number; governed_count: number; unstatused_count: number;
  total_fields: number; total_promote: number;
}
export async function lookup_promotionPreview(entityId: string, globalStatuses: string[], overrides: Record<string,string[]>, acceptObserved: string[] = []): Promise<PromotionPreview> {
  return apiPost(`${_BASE_LKP}/${encodeURIComponent(entityId)}/promotion-preview`, { global_statuses: globalStatuses, overrides, accept_observed: acceptObserved });
}
export async function lookup_syncWithRules(entityId: string, globalStatuses: string[], overrides: Record<string,string[]>, by: string, acceptObserved: string[] = []): Promise<any> {
  return apiPost(`${_BASE_LKP}/${encodeURIComponent(entityId)}/sync-with-rules`, { global_statuses: globalStatuses, overrides, reviewed_by: by, accept_observed: acceptObserved });
}
